'use client';

import * as React from 'react';
import {useI18n} from '@udemy-v2/i18n';
import {Button} from '@udemy-v2/react-core-components';
import {publishEvent} from '@udemy-v2/event-tracking';

import styles from './features-cta.module.scss';
import {PageForType} from '../../types/page';
import {getFeaturesCtaTexts} from './features-cta.constants';
import {
    SubscriptionOfferPresented,
    SubscriptionLearnMoreEvent,
    LandingPageCTASelected,
} from '../events';
import {useRef, useEffect} from 'react';
import {SubscriptionPlan} from '@/generated/graphql';

interface FeaturesCtaProps {
    pageFor: PageForType;
    subscriptionPlans?: SubscriptionPlan[] | null;
    countryCode?: string | null;
}

function FeaturesCta({pageFor, subscriptionPlans, countryCode}: FeaturesCtaProps) {
    const {gettext} = useI18n();
    const {cta} = getFeaturesCtaTexts(gettext)[pageFor];
    const buttonRef = useRef<HTMLDivElement>(null);

    const uiRegion = 'bottom';

    const handleClick = () => {
        const firstPlan = subscriptionPlans?.[0];
        const firstPriceOption = firstPlan?.priceOptions?.[0];
        if (pageFor && pageFor === 'pp') {
            publishEvent(
                new SubscriptionLearnMoreEvent({
                    uiRegion,
                    planPriceId: firstPriceOption?.id || '0',
                    pageFor,
                }),
            );
        } else {
            publishEvent(
                new LandingPageCTASelected({
                    componentName: 'explore_now_' + uiRegion,
                    pageFor: pageFor,
                }),
            );
        }
        window.open(cta.url, '_blank', 'noopener,noreferrer');
    };

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        observer.disconnect();

                        const firstPlan = subscriptionPlans?.[0];
                        const firstPriceOption = firstPlan?.priceOptions?.[0];
                        const price = firstPriceOption?.price?.list?.toString() || '0';
                        const currency = firstPriceOption?.price?.currency?.toString() || 'null';
                        const hasTrial = !!firstPriceOption?.trial?.dateInterval;
                        const trialDays = firstPriceOption?.trial?.dateInterval?.count || 0;
                        const renewalIntervalType =
                            (firstPriceOption?.renewalInterval?.type?.toLowerCase() || 'month') as
                                | 'month'
                                | 'day'
                                | 'year'
                                | 'week';
                        const renewalIntervalCount =
                            Number(firstPriceOption?.renewalInterval?.count) || 0;
                        publishEvent(
                            new SubscriptionOfferPresented({
                                uiRegion: uiRegion,
                                primaryPricingOption: {
                                    price,
                                    listPrice: price,
                                    currency,
                                    renewalIntervalType,
                                    renewalIntervalCount,
                                    hasTrial,
                                    trialDays,
                                },
                                countryCode: countryCode || 'US',
                                pageFor,
                            }),
                        );
                    }
                });
            },
            {threshold: 0.5},
        );

        if (buttonRef.current) {
            observer.observe(buttonRef.current);
        }

        return () => {
            observer.disconnect();
        };
    }, [countryCode, subscriptionPlans, pageFor]);

    return (
        <div className={styles.cta_container}>
            <section className={styles.container_section}>
                <p className={styles.promo_text}>{cta.title}</p>
                <div ref={buttonRef}>
                    <Button
                        onClick={handleClick}
                        udStyle="knockout-solid"
                        size="large"
                        className={styles.button_container}
                    >
                        {cta.buttonText}
                    </Button>
                </div>
            </section>
        </div>
    );
}

export {FeaturesCta};
